import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import { BackLink, Spinner } from '../components/ui';
import {
  api,
  ApiError,
  ensureSession,
  login,
  logout,
  refreshMe,
  register,
  useMe,
} from '../lib/api';
import { formatScore } from '../lib/format';

interface HistoryRow {
  id: string;
  kind: string;
  mode: string;
  status: string;
  created_at: string;
  final_cents: number | null;
  languages: string[] | null;
  parent_match_id: string | null;
}

export function Account() {
  const { t, i18n } = useTranslation();
  const { user, loaded } = useMe();
  const [tab, setTab] = useState<'register' | 'login'>('register');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<HistoryRow[] | null>(null);

  useEffect(() => {
    void refreshMe();
  }, []);
  useEffect(() => {
    if (!user) return;
    api<{ matches: HistoryRow[] }>('GET', '/api/me/matches')
      .then((r) => setHistory(r.matches))
      .catch(() => setHistory([]));
  }, [user]);

  const errText = (e: unknown) => {
    const code = e instanceof ApiError ? e.code : 'generic';
    const key = `account.errors.${code}`;
    const txt = t(key);
    return txt === key ? t('account.errors.generic') : txt;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (tab === 'register') {
        await ensureSession(); // upgrade the guest account in place (history kept)
        await register(username, password, email);
      } else {
        await login(username, password);
      }
      setPassword('');
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return <Spinner label={t('common.loading')} />;

  return (
    <main className="page account">
      <BackLink />
      <h1>{t('account.title')}</h1>
      {user && (
        <section className="card">
          <p>
            <strong>{user.displayName}</strong> —{' '}
            {user.kind === 'guest' ? t('account.guest') : t('account.registered')}
          </p>
          {/* Export and deletion for every account, guests included (their data exists too). */}
          <div className="row wrap">
            <a className="btn ghost small" href="/api/me/export" download>
              {t('account.export')}
            </a>
            {user.kind === 'registered' && (
              <button className="btn ghost small" onClick={() => void logout()}>
                {t('account.logout')}
              </button>
            )}
            <button
              className="btn ghost small"
              onClick={async () => {
                if (!window.confirm(t('account.deleteConfirm'))) return;
                await api('DELETE', '/api/me');
                await refreshMe();
              }}
            >
              {t('account.delete')}
            </button>
          </div>
        </section>
      )}

      {(!user || user.kind === 'guest') && (
        <section className="card">
          <div className="row" role="tablist">
            <button
              role="tab"
              aria-selected={tab === 'register'}
              className={`chip${tab === 'register' ? ' selected' : ''}`}
              onClick={() => setTab('register')}
            >
              {t('account.register')}
            </button>
            <button
              role="tab"
              aria-selected={tab === 'login'}
              className={`chip${tab === 'login' ? ' selected' : ''}`}
              onClick={() => setTab('login')}
            >
              {t('account.login')}
            </button>
          </div>
          {tab === 'register' && <p className="muted small">{t('account.saveHint')}</p>}
          <form onSubmit={(e) => void submit(e)}>
            <label className="field">
              <span>{t('account.username')}</span>
              <input
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                minLength={3}
                maxLength={20}
              />
            </label>
            <label className="field">
              <span>{t('account.password')}</span>
              <input
                type="password"
                autoComplete={tab === 'register' ? 'new-password' : 'current-password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={tab === 'register' ? 10 : 1}
              />
            </label>
            {tab === 'register' && (
              <label className="field">
                <span>{t('account.email')}</span>
                <input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
            )}
            {error && (
              <p className="message error" role="alert">
                {error}
              </p>
            )}
            <button className="btn primary big" type="submit" disabled={busy}>
              {tab === 'register' ? t('account.register') : t('account.login')}
            </button>
          </form>
        </section>
      )}

      {history && history.length > 0 && (
        <section className="card">
          <h2>{t('account.history')}</h2>
          <table className="data">
            <tbody>
              {history.map((h) => (
                <tr key={h.id}>
                  <td>{new Date(h.created_at).toLocaleDateString(i18n.language)}</td>
                  <td>
                    <Link href={`/match/${h.id}`}>{t(`modes.${h.mode}.name`)}</Link>
                  </td>
                  <td>{(h.languages ?? []).map((l) => l.toUpperCase()).join('+')}</td>
                  <td className="num">
                    {h.final_cents !== null ? formatScore(h.final_cents, i18n.language) : h.status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </main>
  );
}
